import { test } from 'ramda';

import { admin, api } from '@framework/constants/endpoints';
import { APPLICATION_PATHS } from '@framework/constants/enums';
import { serverUrlBuilder } from '@framework/server/utils';

import { type Meeting, PublishingState } from '@meetings/details/types';

import { type RouteHandler } from './type';

export enum ONBOARDING_GUIDES {
    CREATE_AGENDA = 'create_agenda',
    CREATE_MEETING = 'create_meeting',
    CREATE_MINUTES = 'create_minutes',
    PUBLISH_AGENDA = 'publish_agenda',
    PUBLISH_MINUTES = 'publish_minutes'
}

export const getRedirectUrl = ({
    pendoGuide,
    roomSlug,
    upcomingMeetings,
    pastMeetings
}: {
    pendoGuide: ONBOARDING_GUIDES;
    roomSlug: string | undefined;
    upcomingMeetings: Meeting[];
    pastMeetings: Meeting[];
}) => {
    if (!roomSlug) {
        return null;
    }

    if (pendoGuide === ONBOARDING_GUIDES.CREATE_MEETING) {
        return admin.meetings(roomSlug);
    }

    const meetingsWithAgendaItems = upcomingMeetings?.filter(
        (meeting) => meeting.topics?.length > 0
    );
    if (pendoGuide === ONBOARDING_GUIDES.CREATE_AGENDA) {
        // redirect to the next upcoming meeting
        if (!upcomingMeetings.length) return null;
        return admin.meeting(roomSlug, 'upcoming', upcomingMeetings[0].id, 'agenda');
    }

    if (pendoGuide === ONBOARDING_GUIDES.PUBLISH_AGENDA) {
        // redirect to the next upcoming meeting with agenda items, but where the agenda is not published
        const meetingsWithAgendaUnpublished = meetingsWithAgendaItems.filter(
            (meeting) => meeting.lastPublished === null
        );
        if (!meetingsWithAgendaUnpublished.length) {
            const fallbackMeetings = upcomingMeetings.filter(
                (meeting) => meeting.lastPublished === null
            );
            if (!fallbackMeetings.length) return null;
            return admin.meeting(roomSlug, 'upcoming', fallbackMeetings[0].id, 'agenda');
        }
        return admin.meeting(roomSlug, 'upcoming', meetingsWithAgendaUnpublished[0].id, 'agenda');
    }

    if (pendoGuide === ONBOARDING_GUIDES.CREATE_MINUTES) {
        // redirect to the next upcoming meeting with a published agenda, but unpublished minutes
        const meetingsWithAgendaPublished = meetingsWithAgendaItems.filter(
            (meeting) => meeting.lastPublished != null
        );

        if (!meetingsWithAgendaPublished.length) {
            const fallbackMeetings = pastMeetings.filter(
                (meeting) => meeting.lastPublished != null
            );
            if (!fallbackMeetings.length) return null;
            return admin.meeting(roomSlug, 'upcoming', fallbackMeetings[0].id, 'minutes');
        }

        const meetingsWithMinutesUnpublished = meetingsWithAgendaPublished.filter(
            (meeting) => meeting.minutesLastPublished === null
        );

        if (!meetingsWithMinutesUnpublished.length) {
            return admin.meeting(
                roomSlug,
                'upcoming',
                meetingsWithAgendaPublished[0].id,
                'minutes'
            );
        }
        return admin.meeting(roomSlug, 'upcoming', meetingsWithMinutesUnpublished[0].id, 'minutes');
    }

    if (pendoGuide === ONBOARDING_GUIDES.PUBLISH_MINUTES) {
        // redirect to the last published meeting in the past with agenda items and unpublished minutes
        const pastMeetingsWithAgendaItemsAndPublished = pastMeetings?.filter(
            (meeting) => meeting.topics?.length > 0 && meeting.agendaState === PublishingState.Ready
        );
        const pastMeetingsWithMinutesUnpublished = pastMeetingsWithAgendaItemsAndPublished.filter(
            (meeting) => meeting.minutesLastPublished === null
        );

        if (!pastMeetingsWithMinutesUnpublished.length) {
            const publishedUpcomingMeetings = meetingsWithAgendaItems?.filter(
                (meeting) => meeting.agendaState === PublishingState.Ready
            );
            const pastMeetingsWithMinutesPublished = pastMeetings?.filter(
                (meeting) => meeting.minutesLastPublished != null
            );

            let fallBackMeetings;

            if (publishedUpcomingMeetings.length) {
                fallBackMeetings = publishedUpcomingMeetings;
            } else if (pastMeetingsWithMinutesPublished.length) {
                fallBackMeetings = pastMeetingsWithMinutesPublished;
            } else {
                return null;
            }
            return admin.meeting(roomSlug, 'upcoming', fallBackMeetings[0].id, 'minutes');
        }

        return admin.meeting(
            roomSlug,
            'upcoming',
            pastMeetingsWithMinutesUnpublished[0].id,
            'minutes'
        );
    }
    return null;
};

export const onboardingGuard: RouteHandler<[], boolean> = {
    test: test(new RegExp(`/${APPLICATION_PATHS.ONBOARDING}/([^/]+)`)),
    handler: async (_ctx, contextMethods) => {
        const { axiosServer, dropLastVisited, redirect } = contextMethods;
        const { guide } = _ctx.query;
        const pendoGuide = guide as ONBOARDING_GUIDES;

        const { data: userStates } = await axiosServer.get(serverUrlBuilder(api.userState()));
        const lastVisitedUrl = userStates.results.find(
            (userState: { stateName: string }) => userState.stateName === 'rooms_last_visited_ui'
        )?.value;

        const roomSlug = lastVisitedUrl?.split('/rooms/')[1]?.split('/')[0];
        const { data: roomsData } = await axiosServer.get(serverUrlBuilder(api.rooms()));

        const rooms = roomsData.results;
        const activeRoom = roomSlug
            ? rooms.find((room: { slug: any }) => room.slug === roomSlug)
            : rooms[0];

        if (!activeRoom) {
            dropLastVisited();
            return false;
        }

        const fetchUpcomingMeetings = axiosServer.get(serverUrlBuilder(api.meetings()), {
            params: {
                roomId: activeRoom.id,
                orderBy: 'start',
                mode: 'draft',
                upcoming: true,
                limit: 100
            }
        });

        const fetchPastMeetings = axiosServer.get(serverUrlBuilder(api.meetings()), {
            params: {
                roomId: activeRoom.id,
                orderBy: '-start',
                mode: 'draft',
                before: Date.now() / 1000,
                limit: 100
            }
        });

        try {
            const [upcomingResponse, pastResponse] = await Promise.all([
                fetchUpcomingMeetings,
                fetchPastMeetings
            ]);

            const upcomingMeetings = upcomingResponse?.data?.results;
            const pastMeetings = pastResponse?.data?.results;

            const redirectUrl = getRedirectUrl({
                pendoGuide,
                roomSlug: activeRoom.slug,
                upcomingMeetings,
                pastMeetings
            });

            if (redirectUrl) {
                redirect(redirectUrl);
            } else {
                dropLastVisited();
            }
        } catch {
            dropLastVisited();
        }

        return false;
    }
};
